import AddIcon from '@offline-mui/icons-material/Add'
import ArrowBackIcon from '@offline-mui/icons-material/ArrowBack'
import DeleteOutlinedIcon from '@offline-mui/icons-material/DeleteOutlined'
import DownloadIcon from '@offline-mui/icons-material/Download'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@offline-mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { accountingRequest, accountingUrl, formatMoney, jsonInit, round2, when } from '../api'
import { DownloadLabel } from '../download'
import { LeoBrand, LeoHeader } from '../LeoHeader'
import { InvoiceSearchForm, InvoiceSearchResults, lastSearchOutcome, SearchOutcome } from './InvoiceSearch'
import CardPaymentDialog from './CardPaymentDialog'
import NewInvoiceDialog from './NewInvoiceDialog'
import SendInvoiceMenu from './SendInvoiceMenu'

// Same order as Gestion's supplier transactions: date, code, description, then Total / Comm. / Promo / Profit.
const LINE_HEADERS = ['Date', 'Code', 'Item', 'Total', 'Our COMM', 'Agency COMM', 'Profit']
const STATUSES = ['open', 'closed']
// SND-14217 check types. 91 and 93 differ by bank; Finance will confirm the bank names.
const CHECK_TYPES = [
  { code: 98, label: 'Dummy Check' },
  { code: 91, label: 'Agency Commission Check' },
  { code: 93, label: 'Agency Commission Check' },
]

function checkTypeLabel(code: unknown) {
  const found = CHECK_TYPES.find((row) => row.code === Number(code))
  return found ? `${found.code} - ${found.label}` : ''
}

type Money = (value: unknown) => string

// New Invoice screen: line items and payments kept on screen until Create invoice saves them with the header.
export type DraftRows = {
  addLine: (payload: any) => void
  updateLine: (key: string, payload: any) => void
  removeLine: (key: string) => void
  addPayment: (payload: any) => void
  updatePayment: (key: string, payload: any) => void
  removePayment: (key: string) => void
}

function Facts({ rows }: { rows: [string, any][] }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', columnGap: 2, rowGap: 1 }}>
      {rows.map(([label, value]) => (
        <Box key={label} sx={{ display: 'contents' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
          <Typography variant="body2" component="div" sx={{ fontWeight: 700 }}>{value || '—'}</Typography>
        </Box>
      ))}
    </Box>
  )
}

// Supplier code and name on one line, the line's transaction type underneath.
function LineCode({ line }: { line: any }) {
  return (
    <>
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        <Box component="span" sx={{ fontWeight: 700 }}>{line.code || '—'}</Box>
        {line.supplierName ? <Box component="span" sx={{ ml: 1 }}>{line.supplierName}</Box> : null}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Type: <Box component="span" sx={{ fontWeight: 700, color: line.txnType ? 'info.main' : 'text.disabled' }}>{line.txnType || 'Not set'}</Box>
      </Typography>
    </>
  )
}

// The "Needs check" chip opens Add payment for the line; Refund opens a new line pre-filled from it (type RC).
function LineActions({
  line,
  onAddPayment,
  onRefund,
  onRemove,
}: {
  line: any
  onAddPayment: (line: any) => void
  onRefund: (line: any) => void
  onRemove?: (line: any) => void
}) {
  if (line.isDraft) {
    return onRemove ? (
      <Link component="button" type="button" variant="caption" color="error" onClick={() => onRemove(line)} sx={{ fontWeight: 600, mt: 0.5 }}>
        Remove
      </Link>
    ) : null
  }
  const nudge = line.paymentNudge
  // Refund (RC) lines, and lines already refunded, show neither the check nudge nor Refund.
  if (String(line.txnType || '').toUpperCase() === 'RC' || line.refunded) return null
  const canRefund = /^BSP/i.test(String(line.supplierName || '').trim())
  if (!nudge && !canRefund) return null
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {nudge ? (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          clickable
          onClick={() => onAddPayment(line)}
          label={`Needs check ${nudge.checkTypes.join(' or ')}`}
          title={`${nudge.message} Click to add the payment.`}
        />
      ) : null}
      {canRefund ? (
        <Link component="button" type="button" variant="caption" onClick={() => onRefund(line)} sx={{ fontWeight: 600 }}>
          Refund
        </Link>
      ) : null}
    </Stack>
  )
}

function LineItem({ line }: { line: any }) {
  const description = line.description || line.passengerName || ''
  const airline = line.airlineName || line.airlineCode || ''
  return (
    <>
      <Typography variant="body2">{description || '—'}</Typography>
      {line.ticketNumber ? <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>{line.ticketNumber}</Typography> : null}
      {airline ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Airline — {airline}
        </Typography>
      ) : null}
      {line.readOnly ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Closed</Typography> : null}
    </>
  )
}

function StatusSelect({ value, onChange, disabled, label }: { value: string; onChange: (status: string) => void; disabled?: boolean; label: string }) {
  return (
    <TextField
      select
      size="small"
      aria-label={label}
      value={value || 'open'}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      sx={{ minWidth: 110, '& .MuiSelect-select': { py: 0.5, textTransform: 'capitalize', fontWeight: 600, color: value === 'closed' ? 'error.main' : 'success.dark' } }}
    >
      {STATUSES.map((status) => (
        <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>{status}</MenuItem>
      ))}
    </TextField>
  )
}

const EMPTY_LINE_DRAFT = {
  supplierCode: '',
  txnType: '',
  airlineCode: '',
  description: '',
  ticketNumber: '',
  txnValue: '',
  txnOtherTaxes: '',
  txnGst: '',
  txnQst: '',
  txnHst: '',
  txnTotal: '',
  ourValue: '',
  ourGst: '',
  ourQst: '',
  ourHst: '',
  ourTotal: '',
  agencyValue: '',
  agencyGst: '',
  agencyQst: '',
  agencyHst: '',
  agencyTotal: '',
}

function moneyStr(value: unknown) {
  if (value == null || value === '') return ''
  const number = Number(value)
  return Number.isFinite(number) ? String(number) : ''
}

function draftFromLine(line: any) {
  const breakdown = line?.breakdown || {}
  const our = line?.ourCommissionBreakdown || {}
  const agency = line?.agencyCommissionBreakdown || {}
  return {
    ...EMPTY_LINE_DRAFT,
    supplierCode: line?.code || '',
    txnType: line?.txnType || '',
    airlineCode: line?.airlineCode || '',
    description: line?.description || line?.passengerName || '',
    ticketNumber: line?.ticketNumber || '',
    txnValue: moneyStr(breakdown.base),
    txnOtherTaxes: moneyStr(breakdown.otherTaxes),
    txnGst: moneyStr(breakdown.gst),
    txnQst: moneyStr(breakdown.qst),
    txnHst: moneyStr(breakdown.hst),
    ourValue: moneyStr(our.value ?? line?.ourCommission),
    ourGst: moneyStr(our.gst),
    ourQst: moneyStr(our.qst),
    ourHst: moneyStr(our.hst),
    agencyValue: moneyStr(agency.value ?? line?.agencyCommission),
    agencyGst: moneyStr(agency.gst),
    agencyQst: moneyStr(agency.qst),
    agencyHst: moneyStr(agency.hst),
  }
}

// Refund pre-fill: the original amounts with the sign flipped (blank and zero stay as they are).
const REFUND_NEGATED_FIELDS = [
  'txnValue', 'txnOtherTaxes', 'txnGst', 'txnQst', 'txnHst',
  'ourValue', 'ourGst', 'ourQst', 'ourHst',
  'agencyValue', 'agencyGst', 'agencyQst', 'agencyHst',
] as const

function refundDraftFromLine(line: any) {
  const draft: any = { ...draftFromLine(line), txnType: 'RC' }
  for (const key of REFUND_NEGATED_FIELDS) {
    const number = Number(draft[key])
    if (draft[key] !== '' && Number.isFinite(number) && number !== 0) draft[key] = String(round2(-number))
  }
  return draft as typeof EMPTY_LINE_DRAFT
}

function moneyField(value: string) {
  if (value === '' || value == null) return undefined
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

function sumMoney(...values: string[]) {
  return round2(values.reduce((sum, value) => sum + (Number(value) || 0), 0))
}

function MoneyField({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string
  value: string
  onChange?: (value: string) => void
  readOnly?: boolean
}) {
  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={value}
      onChange={onChange && !readOnly ? (event) => onChange(event.target.value) : undefined}
      slotProps={{ htmlInput: { step: '0.01' }, input: readOnly ? { readOnly: true } : undefined }}
    />
  )
}

function LineFormDialog({
  open,
  line,
  template,
  invoiceNumber,
  recordLocator,
  onClose,
  onSaved,
  onDraftSave,
}: {
  open: boolean
  line?: any
  // Refund: a new line pre-filled from this one, with transaction type RC. Saving adds a line.
  template?: any
  invoiceNumber: string
  recordLocator: string
  onClose: () => void
  onSaved: (invoice: any) => void
  // New Invoice screen: hand the line back instead of saving it.
  onDraftSave?: (payload: any) => void
}) {
  const editing = Boolean(line)
  const closed = Boolean(line?.readOnly || String(line?.status || '').toLowerCase() === 'closed')
  const [draft, setDraft] = useState({ ...EMPTY_LINE_DRAFT })
  const [suppliers, setSuppliers] = useState<any[]>([])
  const [txnTypes, setTxnTypes] = useState<any[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setDraft(
      line
        ? draftFromLine(line)
        : template
          ? refundDraftFromLine(template)
          : { ...EMPTY_LINE_DRAFT },
    )
    setError('')
    accountingRequest('/reference/transaction-types')
      // RE is the BSP settlement (supplier side), not a line type.
      .then((rows) => setTxnTypes(Array.isArray(rows) ? rows.filter((row) => row.active !== false && row.code !== 'RE') : []))
      .catch(() => setTxnTypes([]))
    accountingRequest('/reference/suppliers')
      .then((rows) => setSuppliers(Array.isArray(rows) ? rows.filter((row) => row.active !== false) : []))
      .catch(() => setSuppliers([]))
  }, [open, line, template])

  const txnTotal = useMemo(
    () => (draft.txnTotal !== '' ? Number(draft.txnTotal) || 0 : sumMoney(draft.txnValue, draft.txnOtherTaxes, draft.txnGst, draft.txnQst, draft.txnHst)),
    [draft.txnTotal, draft.txnValue, draft.txnOtherTaxes, draft.txnGst, draft.txnQst, draft.txnHst],
  )
  const ourTotal = useMemo(
    () => (draft.ourTotal !== '' ? Number(draft.ourTotal) || 0 : sumMoney(draft.ourValue, draft.ourGst, draft.ourQst, draft.ourHst)),
    [draft.ourTotal, draft.ourValue, draft.ourGst, draft.ourQst, draft.ourHst],
  )
  const agencyTotal = useMemo(
    () => (draft.agencyTotal !== '' ? Number(draft.agencyTotal) || 0 : sumMoney(draft.agencyValue, draft.agencyGst, draft.agencyQst, draft.agencyHst)),
    [draft.agencyTotal, draft.agencyValue, draft.agencyGst, draft.agencyQst, draft.agencyHst],
  )

  function setField(key: keyof typeof EMPTY_LINE_DRAFT, value: string) {
    if (closed) return
    setDraft((current) => ({ ...current, [key]: value }))
  }

  // GST is entered by the user; changing a value only clears the stored total so it is summed again.
  function onOurValueChange(value: string) {
    if (closed) return
    setDraft((current) => ({ ...current, ourValue: value, ourTotal: '' }))
  }

  function onAgencyValueChange(value: string) {
    if (closed) return
    setDraft((current) => ({ ...current, agencyValue: value, agencyTotal: '' }))
  }

  async function save(event: any) {
    event.preventDefault()
    if (closed) return
    if (!draft.supplierCode) {
      setError('Supplier code is required.')
      return
    }
    const supplier = suppliers.find((row) => String(row.code).toUpperCase() === draft.supplierCode.toUpperCase())
    const ourValue = moneyField(draft.ourValue) ?? 0
    const agencyValue = moneyField(draft.agencyValue) ?? 0
    const payload = {
      // A refund records the line it refunds, so that line stops offering Refund and the check nudge.
      refundOf: !editing && template?.lineIdentifier ? template.lineIdentifier : undefined,
      supplierCode: draft.supplierCode,
      txnType: draft.txnType || null,
      supplierName: supplier?.name_en || supplier?.name || line?.supplierName || draft.supplierCode,
      airlineCode: draft.airlineCode.trim() || undefined,
      description: draft.description.trim() || undefined,
      ticketNumber: draft.ticketNumber.trim() || undefined,
      status: line?.status || 'Open',
      date: editing ? undefined : new Date().toISOString(),
      profit: round2(ourValue - agencyValue),
      breakdown: {
        value: moneyField(draft.txnValue) ?? 0,
        otherTaxes: moneyField(draft.txnOtherTaxes) ?? 0,
        gst: moneyField(draft.txnGst) ?? 0,
        qst: moneyField(draft.txnQst) ?? 0,
        hst: moneyField(draft.txnHst) ?? 0,
        total: txnTotal,
      },
      ourCommissionBreakdown: {
        value: ourValue,
        gst: moneyField(draft.ourGst) ?? 0,
        qst: moneyField(draft.ourQst) ?? 0,
        hst: moneyField(draft.ourHst) ?? 0,
        total: ourTotal,
      },
      agencyCommissionBreakdown: {
        value: agencyValue,
        gst: moneyField(draft.agencyGst) ?? 0,
        qst: moneyField(draft.agencyQst) ?? 0,
        hst: moneyField(draft.agencyHst) ?? 0,
        total: agencyTotal,
      },
    }
    if (onDraftSave) {
      onDraftSave(payload)
      onClose()
      return
    }
    setSaving(true)
    setError('')
    try {
      const path = editing
        ? `/invoices/${encodeURIComponent(recordLocator)}/lines/${encodeURIComponent(line.lineIdentifier)}`
        : `/invoices/${encodeURIComponent(recordLocator)}/lines`
      const invoice = await accountingRequest(path, jsonInit(editing ? 'PUT' : 'POST', payload))
      onSaved(invoice)
      onClose()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box component="form" onSubmit={save}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? (line.lineIdentifier || 'Line item') : template ? 'Refund line item' : 'Add line item'}
          {closed ? <Chip size="small" label="Closed · read only" sx={{ ml: 1.5 }} /> : null}
          {!editing ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
              Invoice {invoiceNumber}
              {template?.lineIdentifier ? ` · refund of ${template.lineIdentifier}` : ''}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

          <Typography variant="overline" color="info.main" sx={{ fontWeight: 700 }}>Top section</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              select
              required
              size="small"
              label="Supplier code"
              value={draft.supplierCode}
              disabled={closed}
              onChange={(event) => setField('supplierCode', event.target.value)}
            >
              <MenuItem value="">Select supplier</MenuItem>
              {suppliers.map((row) => (
                <MenuItem key={row.code} value={row.code}>
                  {row.code} — {row.name_en || row.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Airline code"
              value={draft.airlineCode}
              disabled={closed}
              onChange={(event) => setField('airlineCode', event.target.value)}
              helperText="Numeric IATA (e.g. 014) stores carrier name"
            />
            <TextField
              size="small"
              label="Description"
              value={draft.description}
              disabled={closed}
              onChange={(event) => setField('description', event.target.value)}
              helperText="Passenger name and other free-text details"
              sx={{ gridColumn: '1 / -1' }}
            />
            <TextField
              size="small"
              label="Ticket number"
              value={draft.ticketNumber}
              disabled={closed}
              onChange={(event) => setField('ticketNumber', event.target.value)}
            />
            <TextField
              select
              size="small"
              label="Transaction type"
              value={draft.txnType}
              disabled={closed}
              onChange={(event) => setField('txnType', event.target.value)}
            >
              <MenuItem value="">Not set</MenuItem>
              {draft.txnType && !txnTypes.some((row) => row.code === draft.txnType) ? (
                <MenuItem value={draft.txnType}>{draft.txnType}</MenuItem>
              ) : null}
              {txnTypes.map((row) => (
                <MenuItem key={row.code} value={row.code}>
                  {row.description ? `${row.code} - ${row.description}` : row.code}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Typography variant="overline" color="info.main" sx={{ display: 'block', mt: 3, fontWeight: 700 }}>Transaction breakdown</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 1 }}>
            <MoneyField label="Value" value={draft.txnValue} onChange={(value) => setField('txnValue', value)} readOnly={closed} />
            <MoneyField label="Other taxes" value={draft.txnOtherTaxes} onChange={(value) => setField('txnOtherTaxes', value)} readOnly={closed} />
            <MoneyField label="GST" value={draft.txnGst} onChange={(value) => setField('txnGst', value)} readOnly={closed} />
            <MoneyField label="QST" value={draft.txnQst} onChange={(value) => setField('txnQst', value)} readOnly={closed} />
            <MoneyField label="HST" value={draft.txnHst} onChange={(value) => setField('txnHst', value)} readOnly={closed} />
            <MoneyField label="Total including taxes" value={txnTotal.toFixed(2)} readOnly />
          </Box>

          <Typography variant="overline" color="info.main" sx={{ display: 'block', mt: 3, fontWeight: 700 }}>Our commission</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 1 }}>
            <MoneyField label="Value" value={draft.ourValue} onChange={onOurValueChange} readOnly={closed} />
            <MoneyField label="GST" value={draft.ourGst} onChange={(value) => setField('ourGst', value)} readOnly={closed} />
            <MoneyField label="QST" value={draft.ourQst} onChange={(value) => setField('ourQst', value)} readOnly={closed} />
            <MoneyField label="HST" value={draft.ourHst} onChange={(value) => setField('ourHst', value)} readOnly={closed} />
            <MoneyField label="Total including taxes" value={ourTotal.toFixed(2)} readOnly />
          </Box>

          <Typography variant="overline" color="info.main" sx={{ display: 'block', mt: 3, fontWeight: 700 }}>Agency commission</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 1 }}>
            <MoneyField label="Value" value={draft.agencyValue} onChange={onAgencyValueChange} readOnly={closed} />
            <MoneyField label="GST" value={draft.agencyGst} onChange={(value) => setField('agencyGst', value)} readOnly={closed} />
            <MoneyField label="QST" value={draft.agencyQst} onChange={(value) => setField('agencyQst', value)} readOnly={closed} />
            <MoneyField label="HST" value={draft.agencyHst} onChange={(value) => setField('agencyHst', value)} readOnly={closed} />
            <MoneyField label="Total including taxes" value={agencyTotal.toFixed(2)} readOnly />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>{closed ? 'Close' : 'Cancel'}</Button>
          {!closed ? (
            <Button type="submit" variant="contained" color="warning" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save line item' : template ? 'Add refund line' : 'Add line item'}
            </Button>
          ) : null}
        </DialogActions>
      </Box>
    </Dialog>
  )
}

const EMPTY_PAYMENT_DRAFT = {
  fopCode: '',
  checkType: '',
  lineIdentifier: '',
  debit: '',
  credit: '',
  reference: '',
  cardFirst6: '',
  cardOrCheck: '',
}

function amountStr(value: unknown) {
  const number = Number(value)
  return Number.isFinite(number) && number !== 0 ? String(number) : ''
}

function draftFromPayment(payment: any) {
  return {
    ...EMPTY_PAYMENT_DRAFT,
    fopCode: payment?.fopCode || '',
    checkType: payment?.checkType != null ? String(payment.checkType) : '',
    lineIdentifier: payment?.lineIdentifier || '',
    debit: amountStr(payment?.debit),
    credit: amountStr(payment?.credit),
    reference: payment?.reference || '',
    cardFirst6: payment?.cardFirst6 || '',
    cardOrCheck: payment?.cardOrCheck || '',
  }
}

function isCardFop(formsOfPayment: any[], code: string) {
  const row = formsOfPayment.find((item) => item.code === code)
  return /^credit\s*cards?$/i.test(String(row?.category || '').trim())
}

function PaymentFormDialog({
  open,
  recordLocator,
  invoiceNumber,
  lines,
  forLine,
  payment,
  onClose,
  onSaved,
  onDraftSave,
}: {
  open: boolean
  recordLocator: string
  invoiceNumber: string
  lines: any[]
  forLine?: any
  payment?: any
  onClose: () => void
  onSaved: (invoice: any) => void
  // New Invoice screen: hand the payment back instead of saving it.
  onDraftSave?: (payload: any) => void
}) {
  const editing = Boolean(payment?.id)
  // Read-only: rows written by a Transfer Agency Commission (SND-14309) or by an Adyen charge or refund (SND-14275).
  const locked = Boolean(payment?.commissionTransferId || payment?.gatewayKind)
  const [draft, setDraft] = useState({ ...EMPTY_PAYMENT_DRAFT })
  const [formsOfPayment, setFormsOfPayment] = useState<any[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const nudge = forLine?.paymentNudge
  const cardFop = isCardFop(formsOfPayment, draft.fopCode)

  useEffect(() => {
    if (!open) return
    const suggested = nudge?.checkTypes?.length === 1 ? String(nudge.checkTypes[0]) : ''
    setDraft(
      payment?.id
        ? draftFromPayment(payment)
        : { ...EMPTY_PAYMENT_DRAFT, lineIdentifier: forLine?.lineIdentifier || '', checkType: suggested },
    )
    setError('')
    accountingRequest('/reference/forms-of-payment')
      .then((rows) => setFormsOfPayment(Array.isArray(rows) ? rows.filter((row) => row.active !== false) : []))
      .catch(() => setFormsOfPayment([]))
  }, [open, forLine, nudge, payment])

  function setField(key: keyof typeof EMPTY_PAYMENT_DRAFT, value: string) {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  async function save(event: any) {
    event.preventDefault()
    if (locked) return
    if (!draft.fopCode) {
      setError('Form of payment code is required.')
      return
    }
    const debit = Number(draft.debit) || 0
    const credit = Number(draft.credit) || 0
    if (!debit && !credit) {
      setError('Enter a debit or a credit amount.')
      return
    }
    if (cardFop && draft.cardFirst6 && !/^\d{6}$/.test(draft.cardFirst6)) {
      setError('Card first 6 must be exactly 6 digits.')
      return
    }
    if (draft.cardOrCheck) {
      if (cardFop && !/^\d{4}$/.test(draft.cardOrCheck)) {
        setError('Card number must be the last 4 digits only.')
        return
      }
      if (!cardFop && !/^[A-Za-z0-9-]{1,20}$/.test(draft.cardOrCheck)) {
        setError('Check number can have up to 20 letters, digits or dashes.')
        return
      }
    }
    const payload = {
      fopCode: draft.fopCode,
      checkType: draft.checkType ? Number(draft.checkType) : null,
      lineIdentifier: draft.lineIdentifier || undefined,
      debit,
      credit,
      reference: draft.reference.trim() || undefined,
      cardOrCheck: draft.cardOrCheck || undefined,
      cardFirst6: cardFop ? draft.cardFirst6 || undefined : undefined,
    }
    if (onDraftSave) {
      onDraftSave({ ...payload, fopCategory: formsOfPayment.find((row) => row.code === draft.fopCode)?.category || '' })
      onClose()
      return
    }
    setSaving(true)
    setError('')
    try {
      const path = editing
        ? `/invoices/${encodeURIComponent(recordLocator)}/payments/${encodeURIComponent(payment.id)}`
        : `/invoices/${encodeURIComponent(recordLocator)}/payments`
      const invoice = await accountingRequest(path, jsonInit(editing ? 'PUT' : 'POST', payload))
      onSaved(invoice)
      onClose()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={save}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Payment' : 'Add payment'}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            Invoice {invoiceNumber}{editing && payment.paidAt ? ` · paid ${when(payment.paidAt)}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {nudge ? <Alert severity="warning" sx={{ mb: 2 }}>{nudge.message}</Alert> : null}
          {payment?.note ? <Alert severity="info" sx={{ mb: 2 }}>{payment.note}</Alert> : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              select
              required
              size="small"
              disabled={locked}
              label="FoP code"
              value={draft.fopCode}
              onChange={(event) => setField('fopCode', event.target.value)}
            >
              <MenuItem value="">Select form of payment</MenuItem>
              {draft.fopCode && !formsOfPayment.some((row) => row.code === draft.fopCode) ? (
                <MenuItem value={draft.fopCode}>{draft.fopCode}</MenuItem>
              ) : null}
              {formsOfPayment.map((row) => (
                <MenuItem key={row.code} value={row.code}>
                  {row.code} - {row.description_en || row.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              disabled={locked}
              label="Check type"
              value={draft.checkType}
              onChange={(event) => setField('checkType', event.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {CHECK_TYPES.map((row) => (
                <MenuItem key={row.code} value={String(row.code)}>{checkTypeLabel(row.code)}</MenuItem>
              ))}
            </TextField>
            <MoneyField label="Debit" value={draft.debit} onChange={(value) => setField('debit', value)} readOnly={locked} />
            <MoneyField label="Credit" value={draft.credit} onChange={(value) => setField('credit', value)} readOnly={locked} />
            <TextField
              size="small"
              disabled={locked}
              label="Reference"
              value={draft.reference}
              onChange={(event) => setField('reference', event.target.value)}
            />
            {cardFop ? (
              <TextField
                size="small"
                disabled={locked}
                label="Card first 6"
                value={draft.cardFirst6}
                onChange={(event) => setField('cardFirst6', event.target.value.replace(/\D/g, '').slice(0, 6))}
                helperText="First 6 digits, used by invoice search"
                slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6, autoComplete: 'off' } }}
              />
            ) : null}
            <TextField
              size="small"
              disabled={locked}
              label="Card/Check Number"
              value={draft.cardOrCheck}
              onChange={(event) =>
                setField(
                  'cardOrCheck',
                  cardFop
                    ? event.target.value.replace(/\D/g, '').slice(0, 4)
                    : event.target.value.replace(/[^A-Za-z0-9-]/g, '').slice(0, 20),
                )
              }
              helperText={cardFop ? 'Last 4 digits of the card' : 'Check number'}
              slotProps={{ htmlInput: { inputMode: cardFop ? 'numeric' : 'text', maxLength: cardFop ? 4 : 20, autoComplete: 'off' } }}
            />
            <TextField
              select
              size="small"
              disabled={locked}
              label="For line item"
              value={draft.lineIdentifier}
              onChange={(event) => setField('lineIdentifier', event.target.value)}
              helperText="Linking a line clears its payment nudge"
              sx={{ gridColumn: '1 / -1' }}
            >
              <MenuItem value="">Not linked to a line</MenuItem>
              {lines.filter((line) => line.lineIdentifier).map((line) => (
                <MenuItem key={line.lineIdentifier} value={line.lineIdentifier}>
                  {line.lineIdentifier} — {line.description || line.supplierName || line.code}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>{locked ? 'Close' : 'Cancel'}</Button>
          {locked ? null : (
            <Button type="submit" variant="contained" color="secondary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save payment' : 'Add payment'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  )
}

// SND-14309: Transfer Agency Commission. The popup has the fields of Add payment except debit, credit and line, plus
// up to 50 PNR and amount rows. Saving adds a debit here for the total (with a note of the pairs) and a credit on each
// PNR's invoice, all at once. CH ("Transfer from one file to another") is the default form of payment. What can be
// transferred is what the balance says we owe the agency (delete the open RA debit first).
const MAX_TRANSFER_ROWS = 50
const DEFAULT_TRANSFER_FOP = 'CH'

function TransferCommissionDialog({
  open,
  invoice,
  onClose,
  onSaved,
}: {
  open: boolean
  invoice: any
  onClose: () => void
  onSaved: (invoice: any) => void
}) {
  const [formsOfPayment, setFormsOfPayment] = useState<any[]>([])
  const [fopCode, setFopCode] = useState(DEFAULT_TRANSFER_FOP)
  const [checkType, setCheckType] = useState('')
  const [reference, setReference] = useState('')
  const [cardOrCheck, setCardOrCheck] = useState('')
  const [rows, setRows] = useState<{ pnr: string; amount: string }[]>([{ pnr: '', amount: '' }])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const cardFop = isCardFop(formsOfPayment, fopCode)
  const available = Number(invoice?.transferableCommission) || 0
  const total = round2(rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0))

  useEffect(() => {
    if (!open) return
    setFopCode(DEFAULT_TRANSFER_FOP)
    setCheckType('')
    setReference('')
    setCardOrCheck('')
    setRows([{ pnr: '', amount: '' }])
    setError('')
    accountingRequest('/reference/forms-of-payment')
      .then((list) => setFormsOfPayment(Array.isArray(list) ? list.filter((row) => row.active !== false) : []))
      .catch(() => setFormsOfPayment([]))
  }, [open])

  function setRow(index: number, key: 'pnr' | 'amount', value: string) {
    setRows((current) => current.map((row, at) => (at === index ? { ...row, [key]: value } : row)))
  }

  async function save(event: any) {
    event.preventDefault()
    if (!fopCode) {
      setError('Form of payment code is required.')
      return
    }
    if (rows.some((row) => !row.pnr.trim() || !(Number(row.amount) > 0))) {
      setError('Every row needs a PNR and a transfer amount more than 0.')
      return
    }
    if (total > available) {
      setError(`Transfer total ${formatMoney(total)} is more than the ${formatMoney(available)} this invoice can transfer.`)
      return
    }
    setSaving(true)
    setError('')
    try {
      const updated = await accountingRequest(
        `/invoices/${encodeURIComponent(invoice.recordLocator)}/commission-transfers`,
        jsonInit('POST', {
          fopCode,
          checkType: checkType ? Number(checkType) : null,
          reference: reference.trim() || undefined,
          cardOrCheck: cardOrCheck || undefined,
          transfers: rows.map((row) => ({ pnr: row.pnr.trim().toUpperCase(), amount: row.amount })),
        }),
      )
      onSaved(updated)
      onClose()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={save}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Transfer Agency Commission
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            Invoice {invoice?.invoiceNumber} · can transfer {formatMoney(available)} (what the balance owes the agency)
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField select required size="small" label="FoP code" value={fopCode} onChange={(event) => setFopCode(event.target.value)}>
              {fopCode && !formsOfPayment.some((row) => row.code === fopCode) ? <MenuItem value={fopCode}>{fopCode}</MenuItem> : null}
              {formsOfPayment.map((row) => (
                <MenuItem key={row.code} value={row.code}>{row.code} - {row.description_en || row.name}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" label="Check type" value={checkType} onChange={(event) => setCheckType(event.target.value)}>
              <MenuItem value="">None</MenuItem>
              {CHECK_TYPES.map((row) => (
                <MenuItem key={row.code} value={String(row.code)}>{checkTypeLabel(row.code)}</MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Reference" value={reference} onChange={(event) => setReference(event.target.value)} />
            <TextField
              size="small"
              label="Card/Check Number"
              value={cardOrCheck}
              onChange={(event) =>
                setCardOrCheck(cardFop ? event.target.value.replace(/\D/g, '').slice(0, 4) : event.target.value.replace(/[^A-Za-z0-9-]/g, '').slice(0, 20))
              }
              helperText={cardFop ? 'Last 4 digits of the card' : 'Check number'}
              slotProps={{ htmlInput: { maxLength: cardFop ? 4 : 20, autoComplete: 'off' } }}
            />
          </Box>
          <Typography variant="overline" color="secondary.main" sx={{ display: 'block', fontWeight: 700, mt: 2 }}>Transfer to</Typography>
          <Stack spacing={1.25}>
            {rows.map((row, index) => (
              <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <TextField
                  size="small"
                  label="PNR"
                  value={row.pnr}
                  onChange={(event) => setRow(index, 'pnr', event.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6))}
                  slotProps={{ htmlInput: { maxLength: 6, autoComplete: 'off' } }}
                  sx={{ flex: 1 }}
                />
                <Box sx={{ flex: 1 }}>
                  <MoneyField label="Transfer amount" value={row.amount} onChange={(value) => setRow(index, 'amount', value)} />
                </Box>
                <IconButton
                  size="small"
                  aria-label={`Remove row ${index + 1}`}
                  disabled={rows.length === 1}
                  onClick={() => setRows((current) => current.filter((_, at) => at !== index))}
                >
                  <DeleteOutlinedIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={rows.length >= MAX_TRANSFER_ROWS}
              onClick={() => setRows((current) => [...current, { pnr: '', amount: '' }])}
            >
              Add PNR ({rows.length} of {MAX_TRANSFER_ROWS})
            </Button>
            <Typography variant="body2" sx={{ fontWeight: 700, color: total > available ? 'error.main' : 'text.primary' }}>
              Total {formatMoney(total)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Adds a credit on this invoice for the total, with a note of each PNR and amount, and a debit on each PNR's
            invoice. All are saved together, or none if anything fails.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" color="secondary" disabled={saving || !available}>
            {saving ? 'Transferring…' : 'Transfer'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

function LinesTable({
  lines,
  money,
  invoiceNumber,
  recordLocator,
  onInvoiceChange,
  onAddPayment,
  disabled = false,
  draft,
}: {
  lines: any[]
  money: Money
  invoiceNumber: string
  recordLocator: string
  onInvoiceChange: (invoice: any) => void
  onAddPayment: (line: any) => void
  disabled?: boolean
  draft?: DraftRows
}) {
  const [selected, setSelected] = useState<any>(null)
  const [adding, setAdding] = useState(false)
  const [refundOf, setRefundOf] = useState<any>(null)
  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="overline" color="info.main" sx={{ fontWeight: 700 }}>Line items</Typography>
        <Button
          variant="contained"
          color="warning"
          startIcon={<AddIcon />}
          disabled={disabled}
          onClick={() => {
            setSelected(null)
            setRefundOf(null)
            setAdding(true)
          }}
        >
          Add line item
        </Button>
      </Stack>
      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'info.main' }}>
              {LINE_HEADERS.map((label, index) => (
                <TableCell key={label} align={index < 3 ? 'left' : 'right'} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, index) => (
              <TableRow key={line.lineIdentifier || `${line.ticketNumber || line.code}-${index}`} sx={{ bgcolor: index % 2 ? 'rgba(29, 78, 137, 0.06)' : 'background.paper' }}>
                <TableCell>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => {
                      setAdding(false)
                      setRefundOf(null)
                      setSelected(line)
                    }}
                    sx={{ fontWeight: 600 }}
                  >
                    {when(line.date) || '—'}
                  </Link>
                </TableCell>
                <TableCell><LineCode line={line} /></TableCell>
                <TableCell>
                  <LineItem line={line} />
                  <LineActions
                    line={line}
                    onAddPayment={onAddPayment}
                    onRefund={(source) => {
                      setAdding(false)
                      setSelected(null)
                      setRefundOf(source)
                    }}
                    onRemove={draft ? (source) => draft.removeLine(source.draftKey) : undefined}
                  />
                </TableCell>
                <TableCell align="right">{line.kind === 'loyalty' ? '' : money(line.total)}</TableCell>
                <TableCell align="right">{money(line.ourCommission)}</TableCell>
                <TableCell align="right">{money(line.agencyCommission)}</TableCell>
                <TableCell align="right" sx={{ color: Number(line.profit) < 0 ? 'error.main' : 'success.dark', fontWeight: 700 }}>
                  {money(line.profit)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <LineFormDialog
          open={adding || Boolean(selected) || Boolean(refundOf)}
          line={adding || refundOf ? undefined : selected}
          template={refundOf || undefined}
          invoiceNumber={invoiceNumber}
          recordLocator={recordLocator}
          onClose={() => {
            setAdding(false)
            setSelected(null)
            setRefundOf(null)
          }}
          onSaved={onInvoiceChange}
          onDraftSave={
            draft
              ? (payload) => (selected?.isDraft && !adding ? draft.updateLine(selected.draftKey, payload) : draft.addLine(payload))
              : undefined
          }
        />
      </TableContainer>
    </Box>
  )
}

// Summary of an invoice; empty (zeros) before the invoice exists on the New Invoice screen.
// SND-14328: two sections, Balance (what is still owed either way) and Profit; each ends on its total in bold.
export function SummaryCard({ invoice }: { invoice: any | null }) {
  const money: Money = (value) => formatMoney(value, invoice?.flightType)
  const summary = invoice?.summary || {}
  const amount = (value: any) => Number(value) || 0
  const less = (value: any) => (amount(value) ? -amount(value) : 0)
  const sections: [string, number, boolean][][] = [
    [
      ['Sales', amount(summary.sales), false],
      ['less Payments', less(summary.payments), false],
      ['less Agency commission', less(summary.agencyCommission), false],
      ['plus Payable to agency', amount(summary.payableToAgency), false],
      ['Balance', amount(summary.balance), true],
    ],
    [
      ['Our commission', amount(summary.ourCommission), false],
      ['less Agency commission', less(summary.agencyCommission), false],
      ['less Loyalty points', less(summary.loyaltyDollars), false],
      ['Profit', amount(summary.profitAfterLoyalty), true],
    ],
  ]
  return (
    <Card sx={{ borderTop: '4px solid', borderTopColor: 'warning.main', bgcolor: '#fff8ec', flex: 1, display: 'flex' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="overline" color="warning.dark">Summary</Typography>
        <Stack spacing={2} sx={{ flex: 1, justifyContent: 'space-evenly' }}>
          {sections.map((rows) => (
            <Stack key={rows[rows.length - 1][0]} spacing={0.75}>
              {rows.map(([label, value, total], index) => (
                <Stack
                  key={label}
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                    borderBottom: total ? 0 : rows[index + 1]?.[2] ? 2 : 1,
                    borderColor: 'warning.light',
                    pb: total ? 0 : 0.5,
                  }}
                >
                  <Typography variant="body2" color={total ? 'text.primary' : 'text.secondary'} sx={{ fontWeight: total ? 700 : 400 }}>
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: (total && label === 'Balance' ? value !== 0 : value < 0) ? 'error.main' : 'text.primary' }}
                  >
                    {money(value)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

// SND-14268: Finance's entries for the BSP settlement (RE): when the BSP closed, what the airline paid, the check number.
// Payable, check amount and the linked lines come from the invoice's BSP lines.
function SupplierTransactionDialog({
  open,
  invoice,
  settlement,
  money,
  onClose,
  onSaved,
}: {
  open: boolean
  invoice: any
  settlement: any
  money: Money
  onClose: () => void
  onSaved: (invoice: any) => void
}) {
  const [closedAt, setClosedAt] = useState('')
  const [paid, setPaid] = useState('')
  const [reference, setReference] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setClosedAt(String(settlement?.bspClosedAt || '').slice(0, 16))
    setPaid(settlement?.paid ? String(Math.abs(Number(settlement.paid))) : '')
    setReference(settlement?.reference || '')
    setError('')
  }, [open, settlement])

  async function save(event: any) {
    event.preventDefault()
    if (paid && !/^\d+(\.\d{1,2})?$/.test(paid)) {
      setError('Paid amount must be 0 or more with up to 2 decimals; it is shown as a negative.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const updated = await accountingRequest(
        `/invoices/${encodeURIComponent(invoice.recordLocator)}/supplier-transaction`,
        jsonInit('PUT', { bspClosedAt: closedAt || null, paidAmount: paid || 0, reference: reference.trim() || null }),
      )
      setSaving(false)
      onSaved(updated)
      onClose()
    } catch (err: any) {
      setError(err.message)
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={save}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          BSP settlement (RE)
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            Invoice {invoice?.invoiceNumber} · check type 98 · linked to {settlement?.linkedLines?.length || 0} BSP line
            {settlement?.linkedLines?.length === 1 ? '' : 's'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              size="small"
              type="datetime-local"
              label="BSP closed"
              value={closedAt}
              onChange={(event) => setClosedAt(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              size="small"
              label="Check number"
              value={reference}
              onChange={(event) => setReference(event.target.value.replace(/[^A-Za-z0-9-]/g, '').slice(0, 20))}
              slotProps={{ htmlInput: { maxLength: 20, autoComplete: 'off' } }}
            />
            <MoneyField label="Paid by the airline in BSP" value={paid} onChange={setPaid} />
            <TextField size="small" label="Payable (−our commission)" value={money(settlement?.payable)} slotProps={{ input: { readOnly: true } }} />
            <TextField
              size="small"
              label="Check amount (our − agency commission)"
              value={money(settlement?.checkAmount)}
              slotProps={{ input: { readOnly: true } }}
              sx={{ gridColumn: '1 / -1' }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Enter what the airline paid as a positive amount; it is shown as a negative. Payable and check amount follow
            the BSP lines.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" color="info" disabled={saving}>
            {saving ? 'Saving…' : 'Save settlement'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

// SND-14268: supplier side, after the line items. Rows appear only once a close writes them: the BSP settlement (RE)
// from BSP close and one PF per supplier from a Monthly close of fees and points (read-only here), then dummy checks
// (check type 98).
function SupplierTransactions({
  invoice,
  money,
  onInvoiceChange,
  onOpenPayment,
}: {
  invoice: any
  money: Money
  onInvoiceChange: (invoice: any) => void
  onOpenPayment: (payment: any) => void
}) {
  const [settlementOpen, setSettlementOpen] = useState(false)
  const settlements: any[] = invoice?.supplierTransactions || []
  const dummyChecks: any[] = (invoice?.payments || []).filter((payment: any) => Number(payment.checkType) === 98)
  const signed = (value: unknown) => (Number(value) ? money(value) : '')
  return (
    <>
      <Typography variant="overline" color="info.main" sx={{ display: 'block', fontWeight: 700, mt: 3, mb: 1.5 }}>
        Supplier Transactions
      </Typography>
      <SupplierTransactionDialog
        open={settlementOpen}
        invoice={invoice}
        settlement={settlements.find((row) => row.kind !== 'supplier')}
        money={money}
        onClose={() => setSettlementOpen(false)}
        onSaved={onInvoiceChange}
      />
      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'info.main' }}>
              {['Date/Time', 'Type', 'Payable', 'Paid', 'Reference', 'Check type', 'Check amount', 'Linked to'].map((label, index) => (
                <TableCell key={label} align={index >= 2 && index <= 3 ? 'right' : index === 6 ? 'right' : 'left'} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {settlements.map((row) => (
              <TableRow key={`re-${row.supplierCode || 'bsp'}`}>
                <TableCell>
                  {row.kind === 'supplier' ? (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{when(row.bspClosedAt) || '—'}</Typography>
                  ) : (
                    <Link component="button" type="button" variant="body2" onClick={() => setSettlementOpen(true)} sx={{ fontWeight: 600 }}>
                      {row.bspClosedAt ? when(row.bspClosedAt) : 'BSP not closed'}
                    </Link>
                  )}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{row.txnType}</TableCell>
                <TableCell align="right">{signed(row.payable)}</TableCell>
                <TableCell align="right">{signed(row.paid)}</TableCell>
                <TableCell>{row.reference || '—'}</TableCell>
                <TableCell>{checkTypeLabel(row.checkType)}</TableCell>
                <TableCell align="right">{money(row.checkAmount)}</TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {row.kind === 'supplier'
                      ? `${row.supplierCode} ${row.supplierName}: ${row.linkedLines.length === 1 ? row.linkedLines[0] : `${row.linkedLines.length} lines`}`
                      : row.linkedLines.length === 1 ? row.linkedLines[0] : `All ${row.linkedLines.length} BSP lines`}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {dummyChecks.map((payment) => (
              <TableRow key={`dummy-${payment.id}`}>
                <TableCell>
                  <Link component="button" type="button" variant="body2" onClick={() => onOpenPayment(payment)} sx={{ fontWeight: 600 }}>
                    {when(payment.paidAt) || '—'}
                  </Link>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{payment.fopCode}</TableCell>
                <TableCell />
                <TableCell />
                <TableCell>{payment.cardOrCheckDisplay || payment.reference || '—'}</TableCell>
                <TableCell>{checkTypeLabel(payment.checkType)}</TableCell>
                <TableCell align="right">{money(Number(payment.debit) || Number(payment.credit))}</TableCell>
                <TableCell><Typography variant="caption">{payment.lineIdentifier || '—'}</Typography></TableCell>
              </TableRow>
            ))}
            {!settlements.length && !dummyChecks.length ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">No supplier transactions yet: they are added when the BSP or the month is closed.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

// Client transactions and line items of an invoice, then the supplier transactions. On the New Invoice screen (draft)
// the rows stay on screen and the forms hand them back instead of saving; Create invoice saves them with the header.
export function InvoiceActivity({
  invoice,
  onInvoiceChange,
  draft,
}: {
  invoice: any | null
  onInvoiceChange: (invoice: any) => void
  draft?: DraftRows
}) {
  const [paymentDialog, setPaymentDialog] = useState<{ open: boolean; line?: any; payment?: any }>({ open: false })
  const [transferOpen, setTransferOpen] = useState(false)
  // SND-14275: Process Payment (charge) and Refund through the Adyen wrapper.
  const [cardDialog, setCardDialog] = useState<{ mode: 'charge' | 'refund'; payment?: any } | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const money: Money = (value) => formatMoney(value, invoice?.flightType)
  const lines: any[] = invoice?.supplierLines || []
  const disabled = !invoice && !draft
  // SND-14268: dummy checks (98) are supplier-side; everything else is what the agency paid or was paid.
  const clientPayments: any[] = (invoice?.payments || []).filter((payment: any) => Number(payment.checkType) !== 98)

  async function deletePayment(payment: any) {
    if (!window.confirm(`Delete the open RA debit of ${money(payment.debit)}? The invoice balance will show it as owed to the agency.`)) return
    setDeletingId(payment.id)
    try {
      onInvoiceChange(
        await accountingRequest(
          `/invoices/${encodeURIComponent(invoice.recordLocator)}/payments/${encodeURIComponent(payment.id)}`,
          { method: 'DELETE' },
        ),
      )
    } catch (err: any) {
      window.alert(err.message)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1.5 }}>
        <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 700 }}>Client Transactions</Typography>
        <Stack direction="row" spacing={1}>
          {invoice && !draft ? (
            <Button variant="outlined" color="secondary" onClick={() => setTransferOpen(true)}>
              Transfer Agency Commission
            </Button>
          ) : null}
          {invoice && !draft ? (
            <Button variant="outlined" color="secondary" onClick={() => setCardDialog({ mode: 'charge' })}>
              Process Payment
            </Button>
          ) : null}
          <Button variant="contained" color="secondary" startIcon={<AddIcon />} disabled={disabled} onClick={() => setPaymentDialog({ open: true })}>
            Add payment
          </Button>
        </Stack>
      </Stack>
      {invoice && !draft ? (
        <TransferCommissionDialog open={transferOpen} invoice={invoice} onClose={() => setTransferOpen(false)} onSaved={onInvoiceChange} />
      ) : null}
      {invoice && !draft ? (
        <CardPaymentDialog
          open={Boolean(cardDialog)}
          mode={cardDialog?.mode || 'charge'}
          invoice={invoice}
          payment={cardDialog?.payment}
          onClose={() => setCardDialog(null)}
          onSaved={onInvoiceChange}
        />
      ) : null}
      {invoice ? (
        <PaymentFormDialog
          open={paymentDialog.open}
          forLine={paymentDialog.line}
          payment={paymentDialog.payment}
          recordLocator={invoice.recordLocator}
          invoiceNumber={invoice.invoiceNumber}
          lines={lines}
          onClose={() => setPaymentDialog({ open: false })}
          onSaved={onInvoiceChange}
          onDraftSave={
            draft
              ? (payload) =>
                  paymentDialog.payment?.isDraft
                    ? draft.updatePayment(paymentDialog.payment.draftKey, payload)
                    : draft.addPayment(payload)
              : undefined
          }
        />
      ) : null}
      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'secondary.main' }}>
              {/* Same order as Gestion's client transactions: Date, Reference, TR, Debit, Credit, Credit card no. */}
              {['Date', 'Reference', 'FOP', 'Debit', 'Credit', 'Card/Check Number', 'Check type', ''].map((label, index) => (
                <TableCell key={label} align={index === 3 || index === 4 ? 'right' : 'left'} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {clientPayments.map((payment: any, index: number) => (
              <TableRow key={payment.id || `${index}-${payment.fopCode}-${payment.reference}`} sx={{ bgcolor: index % 2 ? 'rgba(231, 111, 81, 0.08)' : 'background.paper' }}>
                <TableCell>
                  {payment.id ? (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setPaymentDialog({ open: true, payment })}
                      sx={{ fontWeight: 600 }}
                    >
                      {when(payment.paidAt) || '—'}
                    </Link>
                  ) : (
                    when(payment.paidAt)
                  )}
                  {payment.isDraft && draft ? (
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      color="error"
                      onClick={() => draft.removePayment(payment.draftKey)}
                      sx={{ display: 'block', fontWeight: 600 }}
                    >
                      Remove
                    </Link>
                  ) : null}
                </TableCell>
                <TableCell>
                  {payment.reference}
                  {payment.note ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{payment.note}</Typography>
                  ) : null}
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{payment.fopCode}</TableCell>
                <TableCell align="right">{Number(payment.debit) ? money(payment.debit) : ''}</TableCell>
                <TableCell align="right">{Number(payment.credit) ? money(payment.credit) : ''}</TableCell>
                <TableCell>{payment.cardOrCheckDisplay}</TableCell>
                <TableCell>{checkTypeLabel(payment.checkType)}</TableCell>
                <TableCell align="right">
                  {/* Card payments Intair charged can be refunded through Adyen. */}
                  {payment.refundable && !draft && Number(payment.credit) - Number(payment.refunded || 0) > 0 ? (
                    <Link component="button" type="button" variant="body2" onClick={() => setCardDialog({ mode: 'refund', payment })} sx={{ fontWeight: 600 }}>
                      Refund
                    </Link>
                  ) : null}
                  {/* SND-14309: an open RA debit can be deleted, so its amount shows in the balance and can be transferred. */}
                  {payment.deletable && !draft ? (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      color="error"
                      disabled={deletingId === payment.id}
                      onClick={() => deletePayment(payment)}
                      sx={{ fontWeight: 600 }}
                    >
                      {deletingId === payment.id ? 'Deleting…' : 'Delete'}
                    </Link>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {!clientPayments.length ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">
                    {disabled ? 'Create the invoice to add payments.' : 'No client transactions yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      <LinesTable
        lines={lines}
        money={money}
        invoiceNumber={invoice?.invoiceNumber || ''}
        recordLocator={invoice?.recordLocator || ''}
        onInvoiceChange={onInvoiceChange}
        onAddPayment={(line) => setPaymentDialog({ open: true, line })}
        disabled={disabled}
        draft={draft}
      />
      {invoice && !draft ? (
        <SupplierTransactions
          invoice={invoice}
          money={money}
          onInvoiceChange={onInvoiceChange}
          onOpenPayment={(payment) => setPaymentDialog({ open: true, payment })}
        />
      ) : null}
    </>
  )
}

// /invoice: Import PNR, Create Invoice and search. Nothing is listed until a search runs; opening an invoice goes to
// its own page.
export default function InvoicePage() {
  const history = useHistory()
  const [draft, setDraft] = useState('')
  const [error, setError] = useState('')
  const [importing, setImporting] = useState(false)
  const [newInvoice, setNewInvoice] = useState(false)
  // Back from an invoice shows the last search again (the form keeps its fields the same way).
  const [search, setSearch] = useState<SearchOutcome | null>(lastSearchOutcome)

  function openInvoice(invoiceNumber: string, notice?: string) {
    history.push(`/invoice/${encodeURIComponent(invoiceNumber)}`, notice ? { notice } : undefined)
  }

  // One match opens that invoice; more are listed until the search is cleared.
  function onSearchResults(outcome: SearchOutcome) {
    if (outcome.results.length === 1) {
      openInvoice(outcome.results[0].invoiceNumber)
      return
    }
    setError('')
    setSearch(outcome)
  }

  async function importPnr(event: any) {
    event.preventDefault()
    const pnr = draft.trim().toUpperCase()
    if (!pnr) return
    setImporting(true)
    setError('')
    try {
      const invoice = await accountingRequest('/invoices/import', jsonInit('POST', { recordLocator: pnr }))
      openInvoice(invoice.invoiceNumber)
    } catch (err: any) {
      setError(err.message)
      setImporting(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>
      <LeoHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <LeoBrand />
          <Stack component="form" direction="row" spacing={1} onSubmit={importPnr} sx={{ ml: 'auto', alignItems: 'center' }}>
            <TextField
              size="small"
              value={draft}
              placeholder="PNR"
              slotProps={{ htmlInput: { maxLength: 6, 'aria-label': 'PNR to import' } }}
              onChange={(event) => setDraft(event.target.value.toUpperCase())}
              sx={{ width: 150, bgcolor: 'common.white', borderRadius: 1, input: { textTransform: 'uppercase' } }}
            />
            <Button type="submit" variant="contained" disabled={importing} sx={{ bgcolor: 'common.white', color: 'primary.main', '&:hover': { bgcolor: '#e7f4f3' } }}>
              Import PNR
            </Button>
            <Button
              type="button"
              variant="contained"
              color="secondary"
              disabled={importing}
              onClick={() => setNewInvoice(true)}
              sx={{ '&.Mui-disabled': { opacity: 1, bgcolor: '#efeafb', color: '#3d2f6b' } }}
            >
              Create Invoice
            </Button>
          </Stack>
        </Box>
        <Divider sx={{ my: 2.5, borderColor: 'rgba(255,255,255,0.35)' }} />
        <InvoiceSearchForm onResults={onSearchResults} onClear={() => setSearch(null)} />
      </LeoHeader>

      {importing ? <Typography sx={{ mt: 2 }} color="info.main">Importing {draft}…</Typography> : null}
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      {search ? <InvoiceSearchResults outcome={search} onOpenInvoice={(row) => openInvoice(row.invoiceNumber)} /> : null}

      <NewInvoiceDialog
        open={newInvoice}
        invoice={null}
        onClose={() => setNewInvoice(false)}
        onInvoiceChange={() => {}}
        onCreated={(created) => {
          setNewInvoice(false)
          openInvoice(created.invoiceNumber)
        }}
        onExisting={(existing) => {
          setNewInvoice(false)
          openInvoice(existing.invoiceNumber, `PNR ${existing.recordLocator} already has invoice ${existing.invoiceNumber}; it is shown below.`)
        }}
        renderSummary={(current) => <SummaryCard invoice={current} />}
        renderActivity={(current, onChange, draftRows) => <InvoiceActivity invoice={current} onInvoiceChange={onChange} draft={draftRows} />}
      />
    </Box>
  )
}

// /invoice/:invoiceId: one invoice, found by its invoice number.
// The app's fixed top bars (header + subheader) and the page colour, so a sticky row sits right under the bars and
// hides what scrolls beneath it. Measured because the bars change height with the screen size.
function useFixedTopBars() {
  const [bars, setBars] = useState({ bottom: 0, background: '' })
  useEffect(() => {
    function measure() {
      const fixed = Array.from(document.querySelectorAll<HTMLElement>('.header, .subheader, .header-mobile'))
        .filter((element) => getComputedStyle(element).position === 'fixed')
      setBars({
        bottom: Math.round(fixed.reduce((max, element) => Math.max(max, element.getBoundingClientRect().bottom), 0)),
        background: getComputedStyle(document.body).backgroundColor,
      })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])
  return bars
}

export function InvoiceDetailPage() {
  const history = useHistory()
  const topBars = useFixedTopBars()
  const location = useLocation<{ notice?: string } | undefined>()
  const { invoiceId } = useParams<{ invoiceId: string }>()
  const [invoice, setInvoice] = useState<any>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState('')
  const [editOpen, setEditOpen] = useState(false)
  const [notice, setNotice] = useState(location.state?.notice || '')

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    setInvoice(null)
    accountingRequest(`/invoices/number/${encodeURIComponent(invoiceId)}`)
      .then((found) => {
        if (active) setInvoice(found)
      })
      .catch((err) => {
        if (active) setError(err.message)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [invoiceId])

  async function patchStatus(path: string, status: string) {
    setBusy('status')
    setError('')
    try {
      setInvoice(await accountingRequest(path, jsonInit('PATCH', { status })))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  // SND-14269: the agency invoice PDF (Gestion layout), built by the server from the saved invoice.
  async function downloadPdf() {
    setBusy('pdf')
    setError('')
    try {
      const response = await fetch(accountingUrl(`/invoices/${encodeURIComponent(invoice.recordLocator)}/pdf`))
      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        throw new Error(body.message || 'Could not download the invoice PDF.')
      }
      const url = URL.createObjectURL(await response.blob())
      const link = document.createElement('a')
      link.href = url
      link.download = `invoice-${invoice.invoiceNumber || invoice.recordLocator}.pdf`
      link.click()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  const agency = invoice?.agency || {}
  const office = invoice?.office || {}

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>
      <LeoHeader sx={{ mb: 2 }} />
      {/* The page scrolls as a whole (Leo header included); this row sticks under the app's top bars. */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', position: 'sticky', top: topBars.bottom, zIndex: 10, py: 1.5, bgcolor: topBars.background || 'background.default' }}
      >
        {/* Always back to the invoice search page (/invoice), wherever the invoice was opened from. */}
        <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push('/invoice')}>
          Back to invoice search
        </Button>
        {invoice ? (
          <Button
            variant="contained"
            color="warning"
            size="small"
            startIcon={<DownloadIcon />}
            disabled={busy === 'pdf'}
            onClick={downloadPdf}
            sx={{ ml: 'auto !important' }}
          >
            <DownloadLabel busy={busy === 'pdf'}>Download PDF</DownloadLabel>
          </Button>
        ) : null}
        {invoice ? <SendInvoiceMenu invoice={invoice} onSent={setNotice} /> : null}
      </Stack>
      {loading ? <Typography sx={{ mt: 2 }} color="info.main">Loading invoice {invoiceId}…</Typography> : null}
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      {notice ? <Alert severity="info" sx={{ mt: 2 }} onClose={() => setNotice('')}>{notice}</Alert> : null}

      {invoice ? (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr', gap: 2, mt: 3 }}>
            <Card sx={{ borderTop: '4px solid', borderTopColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700 }}>Agency</Typography>
                <Facts
                  rows={[
                    ['Name', agency.name],
                    ['Code', agency.code || invoice.agencyCode],
                    ['Phone', agency.phone],
                    ['Address', agency.address],
                    ['Network ID', agency.networkId],
                    ['Email', agency.email],
                  ]}
                />
                <Stack direction="row" sx={{ alignItems: 'center', mt: 2 }}>
                  <Typography variant="overline" color="info.main" sx={{ fontWeight: 700 }}>Booking</Typography>
                  {invoice.isManual ? (
                    <Button size="small" onClick={() => setEditOpen(true)} sx={{ ml: 'auto' }}>
                      Edit header
                    </Button>
                  ) : null}
                </Stack>
                <Facts
                  rows={[
                    ['Invoice number', invoice.invoiceNumber],
                    ['Status', <StatusSelect label="Invoice status" value={invoice.status} disabled={busy === 'status'} onChange={(status) => patchStatus(`/invoices/${invoice.recordLocator}/status`, status)} />],
                    ['PNR', invoice.shownRecordLocator || invoice.recordLocator],
                    ['User', invoice.userName],
                    ['Provider', `${invoice.providerName || ''} · ${invoice.pcc || ''}`.replace(/^ · | · $/g, '')],
                    ['Flight type', invoice.flightType],
                    ['Departs', when(invoice.trip?.departs || invoice.departureDate)],
                    ['Booked', when(invoice.bookingDate)],
                    ['Ticketed', when(invoice.ticketedAt)],
                    ['Promo', invoice.promoCode],
                  ]}
                />
              </CardContent>
            </Card>
            <Stack spacing={2} sx={{ height: '100%' }}>
              <Card sx={{ borderTop: '4px solid', borderTopColor: 'secondary.main' }}>
                <CardContent>
                  <Typography variant="overline" color="secondary.main" sx={{ fontWeight: 700 }}>Office</Typography>
                  <Facts
                    rows={[
                      ['Office', [office.code, office.name].filter(Boolean).join(' · ')],
                      ['Ticketing agent', office.ticketingAgent],
                      ['Invoicing agent', office.invoicingAgent],
                      ['Reservation agent', office.reservationAgent],
                    ]}
                  />
                </CardContent>
              </Card>
              <SummaryCard invoice={invoice} />
            </Stack>
          </Box>

          <InvoiceActivity invoice={invoice} onInvoiceChange={setInvoice} />

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Amounts are the booking values.
            100 loyalty points equal 1 dollar. Markup stays on the Intair card line and is left out of sales.
          </Typography>
        </>
      ) : null}

      <NewInvoiceDialog
        open={editOpen}
        invoice={editOpen ? invoice : null}
        onClose={() => setEditOpen(false)}
        onInvoiceChange={setInvoice}
        onCreated={() => setEditOpen(false)}
        onExisting={() => setEditOpen(false)}
        renderSummary={(current) => <SummaryCard invoice={current} />}
        renderActivity={(current, onChange, draftRows) => <InvoiceActivity invoice={current} onInvoiceChange={onChange} draft={draftRows} />}
      />
    </Box>
  )
}
